import * as React from 'react';

import { observer } from 'mobx-react';
import { useHistory, useParams } from 'react-router-dom';

import { PlainButton } from 'ui/public/button';
import { FormSubmitEvent } from 'ui/base/types';
import { BackButton } from 'ui/base/button/back_button';
import { StandardPage } from 'ui/base/page/standard_page';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { SwitchInput } from 'ui/standard/input/switch_input/switch_input';

import { ResultKind } from 'services/base/types';
import { IAccountsDomain, Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';

import { Redirect } from 'pages/core/redirect/redirect';

import { ScannerTuningStore } from './scanner_tuning_store';

import baseStyles from './scanner_tuning.module.css';
import Messages from './scanner_tuning_messages';

type ScannerTuningStyles = {
  header: string;
  headerBackButton: string;
  headerTitleText: string;
  headerSaveButton: string;

  content: string;

  section: string;

  field: string;
  fieldLabel: string;
  fieldLabelText: string;
  fieldLabelHint: string;
};

const themedStyles: ThemedStyles<ScannerTuningStyles> = {
  [Theme.DAYLIGHT]: {
    header: baseStyles.header,
    headerBackButton: baseStyles.headerBackButton,
    headerTitleText: baseStyles.daylightHeaderTitleText,
    headerSaveButton: baseStyles.daylightHeaderSaveButton,

    content: baseStyles.content,

    section: baseStyles.daylightSection,

    field: baseStyles.field,
    fieldLabel: baseStyles.fieldLabel,
    fieldLabelText: baseStyles.daylightFieldLabelText,
    fieldLabelHint: baseStyles.daylightFieldLabelHint,
  },
  [Theme.MIDNIGHT]: {
    header: baseStyles.header,
    headerBackButton: baseStyles.headerBackButton,
    headerTitleText: baseStyles.midnightHeaderTitleText,
    headerSaveButton: baseStyles.midnightHeaderSaveButton,

    content: baseStyles.content,

    section: baseStyles.midnightSection,

    field: baseStyles.field,
    fieldLabel: baseStyles.fieldLabel,
    fieldLabelText: baseStyles.midnightFieldLabelText,
    fieldLabelHint: baseStyles.midnightFieldLabelHint,
  },
};

type BaseScannerTuningProps = {
  accounts: IAccountsDomain;
  pangolin: Pangolin;
};
export const BaseScannerTuning = observer(({ accounts, pangolin }: BaseScannerTuningProps) => {
  const history = useHistory();
  const styles = useStyles(themedStyles);

  const [store, setStore] = React.useState(() => new ScannerTuningStore(pangolin));
  React.useEffect(() => { setStore(new ScannerTuningStore(pangolin)); }, [pangolin]);

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();
    const result = await store.busy.perform(() => store.handleSave(accounts));
    if (result.kind === ResultKind.OK) {
      history.goBack();
    }
  }, [store, accounts, history]);

  return (
    <StandardPage>
      <form onSubmit={onSubmit}>
        <div className={styles.header}>
          <div className={styles.headerBackButton}>
            <BackButton/>
          </div>
          <div className={styles.headerTitleText}>
            {Messages.title()}
          </div>
          <PlainButton
            type='submit'
            busy={store.busy}
            reserveBusyIconArea={true}
            className={styles.headerSaveButton}>
            {Messages.save()}
          </PlainButton>
        </div>
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                <div className={styles.fieldLabelText}>
                  {Messages.enableScanMdns()}
                  <div className={styles.fieldLabelHint}>
                    {Messages.enableScanMdnsHint()}
                  </div>
                </div>
              </div>
              <SwitchInput
                disabled={store.busy.value}
                store={store.enableScanMdns} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                <div className={styles.fieldLabelText}>
                  {Messages.enableScanNbios()}
                  <div className={styles.fieldLabelHint}>
                    {Messages.enableScanNbiosHint()}
                  </div>
                </div>
              </div>
              <SwitchInput
                disabled={store.busy.value}
                store={store.enableScanNbios} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                <div className={styles.fieldLabelText}>
                  {Messages.enableScanNmap()}
                  <div className={styles.fieldLabelHint}>
                    {Messages.enableScanNmapHint()}
                  </div>
                </div>
              </div>
              <SwitchInput
                disabled={store.busy.value}
                store={store.enableScanNmap} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                <div className={styles.fieldLabelText}>
                  {Messages.enableScanRdns()}
                  <div className={styles.fieldLabelHint}>
                    {Messages.enableScanRdnsHint()}
                  </div>
                </div>
              </div>
              <SwitchInput
                disabled={store.busy.value}
                store={store.enableScanRdns} />
            </div>
            <div className={styles.field}>
              <div className={styles.fieldLabel}>
                <div className={styles.fieldLabelText}>
                  {Messages.enableScanUpnp()}
                  <div className={styles.fieldLabelHint}>
                    {Messages.enableScanUpnpHint()}
                  </div>
                </div>
              </div>
              <SwitchInput
                disabled={store.busy.value}
                store={store.enableScanUpnp} />
            </div>
          </div>
        </div>
      </form>
    </StandardPage>
  );
});

export const ScannerTuning = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  return (
    <BaseScannerTuning
      accounts={accounts}
      pangolin={pangolin}
    />
  );
});
